import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './CardCompare.css';
import { TCreditCard } from '../../types/models/CreditCard';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { ButtonWrapper, LinkWrapper } from '../LinkWrapper/LinkWrapper';
import withAsyncImport from '../../withAsyncImport';
import { Row, Col, Title, Icon, Section, Button } from '../../importCL';
import { useWidthContext } from '../../providers/Width';
import { cleanInternalLinkUrl } from '../../utils/cob-utils';

const CardDrawer = withAsyncImport(() => import('../CardDrawer/CardDrawer'));

interface ICardCompareProps extends MappedComponentProperties {
    text?: string;
    defaultCards?: string[];
    creditCards?: TCreditCard[];
    parentUrl?: string;
    toolTip?: string;
    useUpdated?: boolean;
}

export const CardCompareEditConfig = {
    emptyLabel: 'Card Compare',
    isEmpty: (props: PropsWithChildren<ICardCompareProps>) => {
        return !props || !props.text;
    },
};

export type CardCompareType = TCreditCard | null;

const hasInvalidPurchaseApr = (card: CardCompareType) =>
    card !== null && (card.aprLow === '' || Number(card.aprLow) === 0);
const setDefaultCards = (props: ICardCompareProps) => {
    const shallowCredit: CardCompareType[] = [];
    props.defaultCards?.forEach(card => {
        props.creditCards?.forEach(creditCard => {
            if (card === creditCard.imagePath) {
                shallowCredit.push(creditCard);
            }
        });
    });
    while (shallowCredit.length < 3) {
        shallowCredit.push(null);
    }
    return shallowCredit;
};

const CardCompare = (props: PropsWithChildren<ICardCompareProps>): JSX.Element | null => {

    const [cardsToCompare, setCardsToCompare] = useState<CardCompareType[]>(setDefaultCards(props));
    const [scrolled, setScrolled] = useState(false);
    const [dynamicLinks, setDynamicLinks] = useState<string[]>(new Array(3));
    const visibleCardsRef = useRef<CardCompareType[]>([]);
    const { width } = useWidthContext();

    const getGaugeIcon = (creditStatus: string) => {
        let gaugeIcon = '';
        const rootPath = '/content/dam/cob-corp-acquisition/images/icons';
        creditStatus = creditStatus.replace(/credit/i, '').trim();
        switch (creditStatus) {
            case 'Average':
                gaugeIcon = rootPath + '/icon-creditscore-average.svg';
                break;
            case 'Average-Excellent':
                gaugeIcon = rootPath + '/icon-creditscore-average-excellent.svg';
                break;
            case 'Excellent':
                gaugeIcon = rootPath + '/icon-creditscore-excellent.svg';
                break;
            case 'Rebuilding':
                gaugeIcon = rootPath + '/icon-creditscore-rebuilding.svg';
                break;
            case 'Rebuilding-Average':
                gaugeIcon = rootPath + '/icon-creditscore-rebuilding-average.svg';
                break;
            case 'Rebuilding-Excellent':
                gaugeIcon = rootPath + '/icon-creditscore-rebuilding-excellent.svg';
                break;
        }
        return gaugeIcon;
    };


    function cardsToCompareCallback(card: TCreditCard, isAdded: boolean) {
        const existingIndex = visibleCardsRef.current.findIndex((c) => c && c.imagePath === card.imagePath);
        const updatedCredit: CardCompareType[] = [...cardsToCompare];

        if (existingIndex >= 0) {
            if (isAdded) {
                return;
            }
            updatedCredit[existingIndex] = null;
            setCardsToCompare(updatedCredit);
            return;
        }

       for (let i = 0; i < 3; ++i) {
            if (updatedCredit[i] === null) {
                if (props.creditCards) {
                    const temp = props.creditCards.find(creditCard => creditCard.imagePath === card.imagePath);
                    updatedCredit[i] = temp as TCreditCard;
                }
                break;
            }
        }
        setCardsToCompare(updatedCredit);
    }

    const filterParamCards = (paramCards: string[]) => {
        const shallowCredit: CardCompareType[] = [];
        paramCards.forEach(card => {
            props.creditCards?.forEach(creditCard => {
                if (creditCard.imagePath.includes(card)) {
                    shallowCredit.push(creditCard);
                }
            });
        });
        while (shallowCredit.length < 3) {
            shallowCredit.push(null);
        }
        setCardsToCompare(shallowCredit);

    };

    useEffect(() => {
        const url = new URL(window.location.href);
        const paramString = new URLSearchParams(url.search);
        if (paramString.toString() !== '') {
            const paramCards = paramString.getAll('card');
            filterParamCards(paramCards);

        } else {
            setCardsToCompare(setDefaultCards(props));
        }

        window.addEventListener('scroll', () => {
            if (window.pageYOffset > 100) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        });
    }, []);

    useEffect(() => {
        const temp: string[] = [...dynamicLinks];
        if (width <= 768 && visibleCardsRef.current.length === 3) {
            cardsToCompare.sort((a, b) => {
                if (a === null) {
                    return 1;
                }
                if (b === null) {
                    return -1;
                }
                return 0;
            });
            setCardsToCompare([cardsToCompare[0], cardsToCompare[1], null]);
        }
        visibleCardsRef.current = width > 768 ? cardsToCompare : cardsToCompare.slice(0, 2);
        visibleCardsRef.current.forEach((card, index) => {
            if (card) {
                if (card.staticLink) {
                    temp[index] = card.staticLink;
                } else {
                    temp[index] = card.dynamicTermsLink;
                }
                if (!props.isInEditor && hasInvalidPurchaseApr(card)) {
                    window.location.assign(cleanInternalLinkUrl('/content/cob-corp-acquisition/us/en/404.html'));
                }
            } else {
                temp[index] = '';
            }
        });
        if (temp.every(link => !link) && typeof props.parentUrl === 'string' && !props.isInEditor) {
            window.location.href = props.parentUrl;
            return;
        }
        setDynamicLinks(temp);
    }, [cardsToCompare, width]);
    if (!props.isInEditor && cardsToCompare.some(hasInvalidPurchaseApr)) {
        return null;
    }

    return (
        <div id='card-compare' className={`CardCompare${props.useUpdated ? '' : '--legacy'}`}>
            <CardDrawer cardsToCompareCallback={cardsToCompareCallback} creditCards={props.creditCards} visibleCards={visibleCardsRef.current} cardsToCompare={cardsToCompare} useUpdated={props.useUpdated}
                scrolled={scrolled} modalText={props.text} toolTip={props.toolTip} />
            {scrolled && !props.useUpdated && <div className='CardCompare__spacer'></div>}
            <div className={`CardCompare__infoCard${props.useUpdated ? '' : '--legacy'}`}>
                <div className='CardCompare__infoContainer'>
                    {
                        props.useUpdated ? 
                            (
                                <React.Fragment>
                                    <Row className='CardCompare__row CardCompare__row--title'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ?
                                                <Col key={index} xs={3} md={4} className='CardCompare__col CardCompare__col--title'>
                                                    <div className={`CardCompare__titleContainer ${width <= 768 ? 'pt-4' : ''}`}>
                                                        <h3 className='bold text-base'>REWARDS<sup>†</sup></h3>
                                                    </div>
                                                </Col> :
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ? 
                                                <Col className='CardCompare__col' key={index} xs={3} md={4}>
                                                    <div className='CardCompare__detailContainer'>
                                                        {card.rewardDescriptions.map((description, index) =>
                                                            <p key={index}>{transformHTMLParserLinks(description)}</p>
                                                        )}
                                                    </div>
                                                </Col> : 
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row CardCompare__row--title'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ?
                                                <Col key={index} xs={3} md={4} className='CardCompare__col CardCompare__col--title'>
                                                    <div className='CardCompare__titleContainer'>
                                                        <h3 className='bold text-base'>ANNUAL FEE<sup>†</sup></h3>
                                                    </div>
                                                </Col> :
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ? 
                                                <Col className='CardCompare__col' key={index} xs={3} md={4}>
                                                    <div className='CardCompare__detailContainer'>
                                                        <p key={index}>{transformHTMLParserLinks(card.supplementalPromotionText ? card.supplementalPromotionText : card.annualFeeText)}</p>
                                                    </div>
                                                </Col> : 
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row CardCompare__row--title'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ?
                                                <Col key={index} xs={3} md={4} className='CardCompare__col CardCompare__col--title'>
                                                    <div className='CardCompare__titleContainer'>
                                                        <h3 className='bold text-base'>APR<sup>†</sup></h3>
                                                    </div>
                                                </Col> :
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ? 
                                                <Col className='CardCompare__col' key={index} xs={3} md={4}>
                                                    <div className='CardCompare__detailContainer'>
                                                        <p className='pb-0'>{transformHTMLParserLinks(card.aprLow)}%</p>
                                                        <p className='pt-0'>{transformHTMLParserLinks(card.aprText)}</p>
                                                    </div>
                                                </Col> : 
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row CardCompare__row--title'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ?
                                                <Col key={index} xs={3} md={4} className='CardCompare__col CardCompare__col--title'>
                                                    <div className='CardCompare__titleContainer'>
                                                        <h3 className='bold text-base'>RECOMMENDED FOR<sup>†</sup></h3>
                                                    </div>
                                                </Col> :
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ? 
                                                <Col className='CardCompare__col' key={index} xs={3} md={4}>
                                                    <div className='CardCompare__detailContainer'>
                                                        <ul>
                                                            {card.recommendedFeatures.map((feature, index) =>
                                                                <li key={index}>{transformHTMLParserLinks(feature)}</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </Col> : 
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row CardCompare__row--title'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ?
                                                <Col key={index} xs={3} md={4} className='CardCompare__col CardCompare__col--title'>
                                                    <div className='CardCompare__titleContainer'>
                                                        <h3 className='bold text-base'>BENEFITS</h3>
                                                    </div>
                                                </Col> :
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row'>
                                        {visibleCardsRef.current.map((card, index) => 
                                            card ? 
                                                <Col className='CardCompare__col' key={index} xs={3} md={4}>
                                                    <div className='CardCompare__detailContainer'>
                                                        <ul>
                                                            {card.benefitDescriptions.map((feature, index) =>
                                                                <li key={index}>{transformHTMLParserLinks(feature)}</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </Col> : 
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Row className='CardCompare__row'>
                                        {visibleCardsRef.current.map((card, index) =>
                                            card ?
                                                <Col className='CardCompare__col' key={index} xs={3} md={4}>
                                                    <hr className='CardCompare__hr p-0 my-0 mx-auto'/>
                                                    <div className='CardCompare__detailContainer py-4 px-4 md:px-6 rounded-bl-2xl rounded-br-2xl'>
                                                        <ButtonWrapper 
                                                            key={index}
                                                            className='CardCompare__termsLink'
                                                            variant='link'
                                                            icon='chevron'
                                                            size={width <= 768 ? 'small' : 'medium'}
                                                            href={dynamicLinks[index]}
                                                            target='_blank'>
                                                                <span>{transformHTMLParserLinks(card.promotionLinkLabel)}</span>
                                                            </ButtonWrapper>
                                                    </div>
                                                </Col>:
                                                <Col key={index} xs={3} md={4} />
                                        )}
                                    </Row>
                                    <Section className='CardCompare__section'>
                                        <Button href={props.parentUrl} variant='solid' size={width <=768 ? 'medium' : 'large'}>View All Cards</Button>
                                    </Section>
                                </React.Fragment> 
                            ) : (
                                <React.Fragment>
                                    <h3
                                        className='CardCompare__title--legacy'>
                                        Overview<sup>†</sup>
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--legacy' key={index}>
                                                <Title className='CardCompare__info--legacy--title'
                                                    tag='h4'>{card && transformHTMLParserLinks(card.overviewSubtitle)}</Title>
                                                <p className='CardCompare__p--legacy'>
                                                    <i>
                                                        {card && transformHTMLParserLinks(card.overviewDescription)}
                                                    </i>
                                                </p>
                                            </Col>,
                                        )}
                                    </Row>
                                    <h3 className='CardCompare__title--legacy'>
                                        Rewards<sup>†</sup>
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--icon' key={index}>
                                                {card && card.rewardImages.map((reward, index) =>
                                                    <React.Fragment key={index}>
                                                        {reward && <Icon loading='lazy' className='CardCompare__icon' src={reward.iconPath} alt={reward.iconAltText} />}
                                                        <p className='CardCompare__p--legacy CardCompare__p--icon'>{transformHTMLParserLinks(card.rewardDescriptions[index])}</p>
                                                    </React.Fragment>,
                                                )}
                                            </Col>,
                                        )}
                                    </Row>
                                    <h3 className='CardCompare__title--legacy'>
                                        Annual Fee<sup>†</sup>
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--centered' key={index}>
                                                <p className='CardCompare__p--legacy'>{card && transformHTMLParserLinks(card.supplementalPromotionText ? card.supplementalPromotionText : card.compareAnnualFeeText)}</p>
                                            </Col>)}
                                    </Row>
                                    <h3 className='CardCompare__title--legacy' >
                                        APR<sup>†</sup>
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--centered' key={index}>
                                                <div>
                                                    <p className='CardCompare__p--legacy'>{card && `${transformHTMLParserLinks(card.aprLow + '%')}`}</p>
                                                    <p className='CardCompare__p--legacy'>{card && transformHTMLParserLinks(card.aprText)}</p>
                                                </div>
                                            </Col>)}
                                    </Row>
                                    <h3 className='CardCompare__title--legacy' >
                                        Credit Level
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--icon' key={index}>
                                                <div className='CardCompare__gauge' >
                                                    {card && <Icon loading='lazy' className='CardCompare__icon--gauge' alt={card.creditStatus}
                                                        src={getGaugeIcon(card.creditStatus)} />}
                                                    <div className='CardCompare__button--legacy'>{card && card.creditStatus}</div>
                                                </div>
                                            </Col>)}
                                    </Row>
                                    <h3 className='CardCompare__title--legacy'>
                                        Key Features<sup>†</sup>
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--icon' key={index}>
                                                <>
                                                    {card && card.keyFeatureImages.map((feature, index) =>
                                                        <React.Fragment key={index}>
                                                            {feature && <Icon loading='lazy' className='CardCompare__icon' src={feature.iconPath} alt={feature.iconAltText} />}
                                                            <p className='CardCompare__p CardCompare__p--icon'>{transformHTMLParserLinks(card.keyFeatureDescriptions[index])}</p>
                                                        </React.Fragment>,
                                                    )}
                                                </>
                                            </Col>)}
                                    </Row>
                                    <h3 className='CardCompare__title--legacy'>
                                        Benefits<sup>†</sup>
                                    </h3>
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--icon' key={index}>
                                                <>
                                                    {card && card.benefitImages.map((benefit, index) =>
                                                        <React.Fragment key={index}>
                                                            {benefit && <Icon loading='lazy' className='CardCompare__icon' src={benefit.iconPath} alt={benefit.iconAltText} />}
                                                            <p className='CardCompare__p CardCompare__p--icon'>{transformHTMLParserLinks(card.benefitDescriptions[index])}</p>
                                                        </React.Fragment>,
                                                    )}
                                                </>
                                            </Col>)}
                                    </Row>
                                    <hr className='CardCompare__hr--legacy' />
                                    <Row className='CardCompare__row--legacy'>
                                        {cardsToCompare.map((card, index) =>
                                            <Col className='CardCompare__info--centered' key={index}>
                                                <LinkWrapper className='CardCompare__button--legacy CardCompare__button--termsLink' key={index}
                                                    target='_blank'
                                                    href={dynamicLinks[index]}>
                                                    {card && transformHTMLParserLinks(card.promotionLinkLabel)}
                                                </LinkWrapper>
                                            </Col>,
                                        )}
                                    </Row>
                                    <Section className='CardCompare__section--legacy'>
                                        <Button href={props.parentUrl} variant='solid' size='medium'>View All Cards</Button>
                                    </Section>
                                </React.Fragment>
                            )
                    }
                    
                </div>
            </div>
        </div>
    );
};

export default CardCompare;
